import {NormalizedOption} from '@wallaby-ui/document';

import {normalizeFreeSoloStrings} from '../encounter/hai/useValueListField';

/* Shared glue for a raw-text value-list HAI list filter (surgery types, user-defined tags, ...).

   Every such filter is a multiselect over the field's `/values`, plus a leading "-" option that maps to
   the backend `<field>IncludeEmpty` flag (keep only HAIs with no value). The select works on a single
   string[] where the "-" sentinel is mixed in with the real values; these helpers convert to/from the
   {values, includeEmpty} pair the filter state and DTO use. Future value-list filters reuse them as-is. */

/** Sentinel option id for the "-" (no value / includeEmpty) choice. Lives at the top of the option list. */
export const VALUE_LIST_UNASSIGNED_ID = '__UNASSIGNED__';

/** Prepends the "-" (includeEmpty) option to a value-list's options. */
export function withUnassignedOption(options: NormalizedOption<string>[], emptyLabel = '-'): NormalizedOption[] {
  const unassignedOption: NormalizedOption = {id: VALUE_LIST_UNASSIGNED_ID, label: emptyLabel, original: null};
  return [unassignedOption, ...options];
}

/** Ensures every currently-selected value has a matching option so its filter chip renders a label, even
   when the value is no longer returned by the `/values` endpoint (e.g. the user deleted it from every HAI
   record while it stays applied in a saved filter). Values missing from `options` are appended as synthetic
   options; without this the select can't resolve a label and renders an empty chip. */
export function withSelectedValues(
  options: NormalizedOption<string>[],
  selectedValues: string[] | undefined,
  getLabel?: (value: string) => string
): NormalizedOption<string>[] {
  if (!selectedValues?.length) return options;
  const seen = new Set(options.map((option) => option.id));
  const extraOptions: NormalizedOption<string>[] = [];
  selectedValues.forEach((value) => {
    if (!seen.has(value)) {
      seen.add(value);
      extraOptions.push({id: value, label: getLabel ? getLabel(value) : value, original: value});
    }
  });
  return extraOptions.length ? [...options, ...extraOptions] : options;
}

/** Builds the select value (real values plus the "-" sentinel when includeEmpty is set). */
export function valueListToSelectValue(values: string[] | undefined, includeEmpty: boolean | undefined): string[] {
  const result = [...(values ?? [])];
  if (includeEmpty) result.push(VALUE_LIST_UNASSIGNED_ID);
  return result;
}

/** Splits a select change back into the real values and the includeEmpty flag.
   An emptied select yields `undefined`, never `[]`: an empty array survives omitNullsy and would be
   serialized into the request as a bare `<field>=` parameter (and would keep the filter counted as active). */
export function selectValueToValueList(newValue: (string | {freeSolo: string})[] | undefined): {
  values: string[] | undefined;
  includeEmpty: boolean | undefined;
} {
  const normalized = normalizeFreeSoloStrings(newValue as any);
  const includeEmpty = normalized.includes(VALUE_LIST_UNASSIGNED_ID) ? true : undefined;
  const values = normalized.filter((v) => v !== VALUE_LIST_UNASSIGNED_ID);
  return {values: values.length ? values : undefined, includeEmpty};
}
